import { useState, useRef, useEffect, ReactNode, CSSProperties, JSX } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface IconProps {
  d: string;
  size?: number;
  stroke?: string;
  fill?: string;
  strokeWidth?: number;
  className?: string;
}

interface Conversation {
  id: number;
  name: string;
  avatar: string | null;
  initials: string;
  color: string;
  time: string;
  lastMsg: string;
  badge: number | null;
  channel: string;
}

type MessageType = "incoming" | "outgoing" | "system" | "system-event" | "awaiting" | "chip";

interface ChatMessage {
  id: number;
  type: MessageType;
  text: string;
  time: string | null;
  status?: string | null;
  label?: string;
}

type InsightTab = "Details" | "Insight" | "Actions";
type MobileView = "list" | "chat";

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon: React.FC<IconProps> = ({ d, size = 16, stroke = "currentColor", fill = "none", strokeWidth = 2, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const Icons = {
  Search: () => <Icon d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />,
  Phone: () => <Icon d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1.2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.08a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.02 16.92z" />,
  MoreVert: () => <Icon d="M12 5v.01M12 12v.01M12 19v.01" strokeWidth={3} />,
  Send: () => <Icon d="M22 2 11 13M22 2 15 22 11 13 2 9l20-7z" />,
  Mic: () => <Icon d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />,
  Attach: () => <Icon d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />,
  ChevronDown: ({ size = 16 }: { size?: number }) => <Icon d="M6 9l6 6 6-6" size={size} />,
  Bot: () => <Icon d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7H3a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2zM5 14v1a7 7 0 0 0 14 0v-1M9 11h.01M15 11h.01" />,
  Whatsapp: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  ),
  SMS: ({ size = 16 }: { size?: number }) => <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" size={size} />,
  Email: ({ size = 16 }: { size?: number }) => <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6" size={size} />,
  Linkedin: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#0077B5">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  Voice: ({ size = 16 }: { size?: number }) => <Icon d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z M19 10v2a7 7 0 0 1-14 0v-2" size={size} />,
  Agents: ({ size = 16 }: { size?: number }) => <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75" size={size} />,
  ArrowRight: ({ size = 16 }: { size?: number }) => <Icon d="M5 12h14M12 5l7 7-7 7" size={size} />,
  Plus: ({ size = 16 }: { size?: number }) => <Icon d="M12 5v14M5 12h14" size={size} />,
  Zap: ({ size = 16 }: { size?: number }) => <Icon d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" size={size} />,
  Clock: () => <Icon d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M12 6v6l4 2" />,
  RCS: ({ size = 16 }: { size?: number }) => <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z M8 10h.01 M12 10h.01 M16 10h.01" size={size} />,
  Sparkles: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M5 3l.75 2.25L8 6l-2.25.75L5 9l-.75-2.25L2 6l2.25-.75L5 3z" />
      <path d="M19 13l.75 2.25L22 16l-2.25.75L19 19l-.75-2.25L16 16l2.25-.75L19 13z" />
    </svg>
  ),
};

// ─── Mock Data ────────────────────────────────────────────────────────────────
const conversations: Conversation[] = [
  { id: 1, name: "+91-9788882888", avatar: null, initials: "P", color: "#6366f1", time: "07:45 am", lastMsg: "Hi, I need pest control for my office.", badge: null, channel: "whatsapp" },
  { id: 2, name: "Karen Castillo", avatar: "https://i.pravatar.cc/40?img=5", initials: "KC", color: "#f43f5e", time: "07:45 am", lastMsg: "🎤 0:14", badge: 2, channel: "whatsapp" },
  { id: 3, name: "Maximillian Jacob", avatar: "https://i.pravatar.cc/40?img=11", initials: "MJ", color: "#f59e0b", time: "07:45 am", lastMsg: "Bro, I have a good idea!", badge: 2, channel: "whatsapp" },
  { id: 4, name: "Martha Craig", avatar: "https://i.pravatar.cc/40?img=9", initials: "MC", color: "#10b981", time: "07:45 am", lastMsg: "📷 Photo", badge: null, channel: "whatsapp" },
  { id: 5, name: "Tabitha Potter", avatar: "https://i.pravatar.cc/40?img=7", initials: "TP", color: "#8b5cf6", time: "07:45 am", lastMsg: "Actually I wanted to check with you", badge: null, channel: "whatsapp" },
  { id: 6, name: "Maisy Humphrey", avatar: "https://i.pravatar.cc/40?img=16", initials: "MH", color: "#ec4899", time: "07:00 am", lastMsg: "Welcome, to make design process", badge: null, channel: "whatsapp" },
  { id: 7, name: "John Doe", avatar: "https://i.pravatar.cc/40?img=3", initials: "JD", color: "#14b8a6", time: "07:00 am", lastMsg: "Ok, have a good trip!", badge: null, channel: "whatsapp" },
  { id: 8, name: "Mikel Larsson", avatar: "https://i.pravatar.cc/40?img=12", initials: "ML", color: "#3b82f6", time: "07:00 am", lastMsg: "Ok, have a good trip!", badge: null, channel: "whatsapp" },
  { id: 9, name: "Kieron Dotson", avatar: "https://i.pravatar.cc/40?img=8", initials: "KD", color: "#f97316", time: "07:00 am", lastMsg: "Ok, have a good trip!", badge: null, channel: "whatsapp" },
];

const initialMessages: ChatMessage[] = [
  { id: 1, type: "incoming", text: "Hi, I need pest control for my office.", time: "2:00pm", status: null },
  { id: 2, type: "system", text: 'Custom field "Order Status" set to "No Ordered" by Ashutosh', time: null },
  { id: 3, type: "outgoing", text: "Hello 👋\nWelcome to ItemSecure. Thank you for reaching out to us!", time: "2:00pm", status: "read" },
  { id: 4, type: "outgoing", text: "We're here to help you with safe, reliable, and professional pest management solutions for your home or business. Our team will guide you through a quick assessment so we can recommend the right treatment and schedule a service at your convenience.", time: "2:00pm", status: "read" },
  { id: 5, type: "outgoing", text: "What do you need help with?", time: "2:00pm", status: "read" },
  { id: 6, type: "system-event", label: "System Event", text: "Catalog Shared: List of Services", time: null },
  { id: 7, type: "awaiting", text: "Awaiting Customer Response", time: null },
  { id: 8, type: "chip", text: "Termite Control", time: "2:00pm" },
  { id: 9, type: "system-event", label: "System Event", text: "Customer Selected: Termite Control", time: null },
  { id: 10, type: "incoming", text: "Got it. Is this for a home or commercial space?", time: "2:01pm", status: null },
];

// ─── Avatar ───────────────────────────────────────────────────────────────────
interface AvatarProps {
  src: string | null;
  initials: string;
  color?: string;
  size?: number;
}

const Avatar: React.FC<AvatarProps> = ({ src, initials, color, size = 38 }) => {
  if (src) return (
    <img
      src={src}
      alt={initials}
      width={size}
      height={size}
      className="rounded-full object-cover flex-shrink-0 border-2 border-white"
      style={{ width: size, height: size }}
    />
  );
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: color || "#6366f1"
      }}
      className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
    >
      <span style={{ fontSize: size * 0.35 }}>{initials}</span>
    </div>
  );
};

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge: React.FC<{ count: number | null }> = ({ count }) => {
  if (!count) return null;
  return (
    <span className="bg-[#25D366] text-white rounded-full text-xs font-bold px-[7px] py-[2px] min-w-[20px] text-center">
      {count}
    </span>
  );
};

// ─── ConvoItem ────────────────────────────────────────────────────────────────
interface ConvoItemProps {
  conv: Conversation;
  selected: boolean;
  onClick: () => void;
}

const ConvoItem: React.FC<ConvoItemProps> = ({ conv, selected, onClick }) => (
  <div
    onClick={onClick}
    className={`flex items-center gap-2.5 px-[14px] py-2.5 cursor-pointer rounded-lg mx-1.5 transition-colors duration-150 ${selected ? "bg-indigo-50" : "hover:bg-slate-50"
      }`}
  >
    <Avatar src={conv.avatar} initials={conv.initials} color={conv.color} />
    <div className="flex-1 min-w-0">
      <div className="flex justify-between items-center">
        <span className="font-semibold text-[13.5px] text-slate-800">{conv.name}</span>
        <span className="text-[11px] text-slate-400">{conv.time}</span>
      </div>
      <div className="flex justify-between items-center mt-0.5">
        <span className="text-xs text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px]">{conv.lastMsg}</span>
        <Badge count={conv.badge} />
      </div>
    </div>
  </div>
);

// ─── ChannelTab ───────────────────────────────────────────────────────────────
interface ChannelTabProps {
  label: string;
  count?: number;
  icon: ReactNode;
  active: boolean;
  onClick: () => void;
}

const ChannelTab: React.FC<ChannelTabProps> = ({ label, count, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 px-3 py-3 rounded-lg font-semibold text-xs whitespace-nowrap ${active
        ? "bg-[#9ED8B5] text-[#1C6E46] border-none"
        : "bg-white text-[#364F61] border border-slate-200"
      }`}
  >
    {icon}
    <span>{label}</span>
    {count !== undefined && (
      <span className={`rounded-full px-[6px] py-px text-[11px] ${active ? "bg-white/30" : "bg-slate-200"
        }`}>{count}</span>
    )}
  </button>
);

// ─── Sidebar ─────────────────────────────────────────────────────────────────
interface SidebarProps {
  selected: Conversation;
  onSelect: (c: Conversation) => void;
  activeTab: number;
  onTabChange: (i: number) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ selected, onSelect, activeTab, onTabChange }) => {
  const [search, setSearch] = useState<string>("");
  const filtered = conversations.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.lastMsg.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-[260px] min-w-[200px] bg-white border-r border-[#e8ecf4] flex flex-col h-full overflow-hidden">
      <div className="p-3 pb-2 border-b border-slate-100 bg-[#EAEDFB]">
        <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border border-slate-200">
          <Icons.Search />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search Conversations"
            className="border-none bg-transparent outline-none text-xs text-slate-600 w-full"
          />
        </div>
      </div>
      <div className="p-2 px-3 flex gap-1.5 bg-[#EAEDFB] border-b border-slate-100">
        {["All 3", "New 3", "Awaiting Reply 3"].map((t, i) => (
          <button
            key={t}
            onClick={() => onTabChange(i)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer ${activeTab === i
                ? "bg-indigo-500 text-white"
                : "bg-slate-100 text-slate-500"
              }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map(c => <ConvoItem key={c.id} conv={c} selected={selected?.id === c.id} onClick={() => onSelect(c)} />)}
      </div>
    </div>
  );
};

// ─── Message ─────────────────────────────────────────────────────────────────
const Message: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  if (msg.type === "system") return (
    <div className="text-center my-2">
      <span className="bg-amber-100 border border-amber-200 rounded-lg text-xs text-amber-800 px-3 py-1 inline-block">{msg.text}</span>
    </div>
  );
  if (msg.type === "system-event") return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 px-[14px] my-1.5 mx-4 text-xs text-slate-500">
      <div className="font-semibold mb-0.5 text-slate-400 text-[11px]">⚙️ {msg.label}</div>
      {msg.text}
    </div>
  );
  if (msg.type === "awaiting") return (
    <div className="text-center my-2">
      <span className="bg-green-50 border border-green-200 rounded-full text-xs text-green-700 px-4 py-1 inline-flex items-center gap-1.5">
        <Icons.Clock /> {msg.text}
      </span>
    </div>
  );
  if (msg.type === "chip") return (
    <div className="flex justify-start my-1 mx-4">
      <span className="bg-purple-100 text-purple-700 rounded-full px-3.5 py-1 text-sm font-semibold">{msg.text}</span>
      <span className="text-[11px] text-slate-400 self-end ml-2">{msg.time}</span>
    </div>
  );
  const isOut = msg.type === "outgoing";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} my-1 mx-4`}>
      <div className={`max-w-[72%] px-3.5 py-2.5 text-sm text-slate-800 leading-relaxed shadow-sm whitespace-pre-wrap ${isOut
          ? "bg-[#dcf8c6] rounded-tl-[4px] rounded-tr-[18px] rounded-br-[18px] rounded-bl-[18px]"
          : "bg-white border border-[#e8ecf4] rounded-tl-[18px] rounded-tr-[4px] rounded-br-[18px] rounded-bl-[18px]"
        }`}>
        {msg.text}
        <div className="flex justify-end items-center gap-1 mt-1">
          <span className="text-[11px] text-slate-400">{msg.time}</span>
          {isOut && <span className="text-blue-400 text-sm">✓✓</span>}
        </div>
      </div>
    </div>
  );
};

// ─── ChatArea ─────────────────────────────────────────────────────────────────
interface ChatAreaProps {
  conv: Conversation;
  onOpenInsights: () => void;
  isMobile: boolean;
}

const ChatArea: React.FC<ChatAreaProps> = ({ conv, onOpenInsights, isMobile }) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState<string>("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = (): void => {
    if (!input.trim()) return;
    const newMsg: ChatMessage = {
      id: Date.now(), type: "outgoing", text: input.trim(),
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), status: "sent",
    };
    setMessages(prev => [...prev, newMsg]);
    setInput("");
  };

  return (
    <div className="flex-1 flex flex-col bg-[#f0f2f5] min-w-0">
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center gap-3 border-b border-[#e8ecf4] shadow-sm">
        <Avatar src={conv.avatar} initials={conv.initials} color={conv.color} />
        <div className="flex-1">
          <div className="font-bold text-sm text-slate-800">{conv.name}</div>
          <div className="flex gap-1.5 mt-0.5">
            {(["CTWA", "New Leads", "Not Ordered"] as const).map((tag, i) => (
              <span
                key={tag}
                className={`text-[11px] font-semibold px-[7px] py-px rounded-full ${i === 0 ? "bg-blue-100 text-blue-700" : i === 1 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                  }`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 text-slate-500">
          <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors duration-150 flex items-center justify-center">
            <Icons.Phone />
          </button>
          <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors duration-150 flex items-center justify-center">
            <Icons.Search />
          </button>
          {isMobile && (
            <button
              className="bg-indigo-500 text-white rounded-lg px-2.5 py-1.5 text-xs font-bold cursor-pointer"
              onClick={onOpenInsights}
            >
              Insights
            </button>
          )}
          <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors duration-150 flex items-center justify-center">
            <Icons.MoreVert />
          </button>
        </div>
      </div>

      {/* Date divider */}
      <div className="text-center pt-3 pb-1.5">
        <span className="bg-white border border-slate-200 rounded-full text-xs text-slate-500 px-3.5 py-[3px]">Today</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto pb-2">
        {messages.map(m => <Message key={m.id} msg={m} />)}
        <div ref={bottomRef} />
      </div>

      {/* View Catalog shortcut */}
      <div className="px-4 pb-1.5 flex gap-2">
        <button className="bg-sky-50 border border-sky-200 rounded-lg px-3.5 py-1.5 text-xs text-sky-800 font-semibold cursor-pointer flex items-center gap-1.5">
          <Icons.Sparkles /> View Catalog
        </button>
      </div>

      {/* Input Row */}
      <div className="bg-white px-3.5 py-2.5 border-t border-[#e8ecf4]">
        <div className="flex items-center gap-2 mb-2">
          <button className="bg-slate-100 rounded-lg border border-slate-200 px-3 py-1 text-xs text-indigo-500 font-semibold cursor-pointer flex items-center gap-1">
            <Icons.Sparkles /> Suggest
          </button>
          <div className="flex-1 flex items-center bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage()}
              placeholder="Write your message..."
              className="flex-1 border-none bg-transparent outline-none text-sm text-slate-800"
            />
            <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors duration-150 flex items-center justify-center">
              <Icons.Mic />
            </button>
            <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors duration-150 flex items-center justify-center">
              <Icons.Attach />
            </button>
          </div>
          <button
            onClick={sendMessage}
            className="bg-indigo-500 text-white border-none rounded-lg px-4 py-2 font-bold text-sm cursor-pointer flex items-center gap-1.5"
          >
            <Icons.Send /> Send
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-9 h-5 rounded-full bg-slate-200 cursor-pointer" />
            <span className="text-xs text-slate-500">Enable Manual Reply</span>
          </div>
          <button className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1 cursor-pointer text-xs font-semibold text-slate-600">
            <Icons.Agents size={14} /> Assign To <Icons.ChevronDown size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Insights Panel ───────────────────────────────────────────────────────────
const serviceFlowSteps: string[] = ["Address Collection", "Schedule Visit", "Work Order Creation", "Payment Confirmation", "Technician Assignment", "Confirm Completion", "Close Job"];
const statusPipelineSteps: string[] = ["New Lead", "Potential First Order", "Reminders", "Recurring Orders", "Qualified"];

interface StepItemProps { label: string; done: boolean; active: boolean; }
const StepItem: React.FC<StepItemProps> = ({ label, done, active }) => (
  <div className="flex items-center gap-2 py-1">
    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${done ? "bg-green-500" : active ? "bg-indigo-500 border-2 border-indigo-300" : "bg-slate-200"
      }`}>
      {done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>}
    </div>
    <span className={`text-xs ${done ? "text-green-500 font-semibold" : active ? "text-indigo-500 font-semibold" : "text-slate-400"
      }`}>{label}</span>
  </div>
);

interface SectionHeaderProps { title: string; defaultOpen?: boolean; children: ReactNode; }
const SectionHeader: React.FC<SectionHeaderProps> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  return (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex justify-between items-center p-3 px-4 bg-transparent border-none cursor-pointer font-bold text-xs text-slate-800"
      >
        {title}
        <span className={`transition-transform duration-200 text-slate-400 ${open ? "rotate-180" : ""}`}>
          <Icons.ChevronDown size={16} />
        </span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

interface RowProps { label: string; value: string; valueColor?: string; }
const Row: React.FC<RowProps> = ({ label, value, valueColor }) => (
  <div className="flex justify-between py-1 text-xs">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold" style={{ color: valueColor || "#1e293b" }}>{value}</span>
  </div>
);

const GreenDot: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-1.5 py-[3px]">
    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
    <span className="text-xs text-slate-600">{label}</span>
  </div>
);

interface InsightsPanelProps {
  activeInsightTab: InsightTab;
  onTabChange: (tab: InsightTab) => void;
}

const InsightsPanel: React.FC<InsightsPanelProps> = ({ activeInsightTab, onTabChange }) => (
  <div className="w-[280px] min-w-[240px] bg-white border-l border-[#e8ecf4] flex flex-col overflow-hidden">
    {/* Header */}
    <div className="px-4 pt-3.5 pb-2.5 border-b border-slate-100">
      <div className="flex items-center gap-1.5 mb-3">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
          <Icons.Bot />
        </div>
        <span className="font-extrabold text-sm text-slate-800">ConvoAI Insights</span>
      </div>
      <div className="flex gap-1 bg-slate-100 rounded-lg p-[3px]">
        {(["Details", "Insight", "Actions"] as InsightTab[]).map(tab => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={`flex-1 py-1.5 rounded-lg border-none cursor-pointer font-semibold text-xs transition-all duration-150 ${activeInsightTab === tab
                ? "bg-white text-indigo-500 shadow-sm"
                : "bg-transparent text-slate-500"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>
    </div>

    {/* Tab Content */}
    <div className="flex-1 overflow-y-auto">
      {activeInsightTab === "Details" && (
        <>
          <SectionHeader title="Lead Info">
            <Row label="Name" value="N.A." />
            <Row label="Phone Number" value="+91-9788882888" />
            <Row label="Source" value="Facebook Ads, CTWA" />
            <Row label="Location" value="Bengaluru, K.A" />
            <Row label="First Contact" value="08:34AM" />
            <Row label="Priority" value="High" valueColor="#ef4444" />
          </SectionHeader>
          <SectionHeader title="Service Flow">
            {serviceFlowSteps.map((s, i) => <StepItem key={s} label={s} done={i === 0} active={i === 1} />)}
          </SectionHeader>
          <SectionHeader title="Status Pipeline">
            {statusPipelineSteps.map((s, i) => <StepItem key={s} label={s} done={i === 0} active={false} />)}
          </SectionHeader>
        </>
      )}

      {activeInsightTab === "Insight" && (
        <>
          <SectionHeader title="Emotional Intents">
            <div className="bg-yellow-100 rounded-lg p-1.5 px-2.5 text-xs text-amber-900 font-semibold flex items-center gap-1.5">
              <Icons.Zap size={14} /> High Purchase Intent Detected
            </div>
          </SectionHeader>
          <SectionHeader title="Key Insights">
            <GreenDot label="Asked about pricing details early" />
            <GreenDot label="Requested a quote" />
            <GreenDot label="High sense of urgency detected for availing service" />
          </SectionHeader>
          <SectionHeader title="Business Insight">
            <Row label="Estimated Job Value" value="₹18,000 – ₹25,000" valueColor="#059669" />
            <Row label="Profitability Tier" value="High Margin Job" valueColor="#7c3aed" />
          </SectionHeader>
          <SectionHeader title="Conversation Health">
            <div className="mb-2">
              <div className="flex justify-between mb-1 text-xs">
                <span className="text-slate-500">Engagement Score</span>
                <span className="font-bold text-green-600">High</span>
              </div>
              <div className="bg-slate-200 rounded-full h-1.5">
                <div className="w-[88%] h-full rounded-full bg-gradient-to-r from-green-500 to-green-300" />
              </div>
            </div>
            <div className="mb-2">
              <div className="flex justify-between mb-1 text-xs">
                <span className="text-slate-500">Conversion Probability</span>
                <span className="font-bold text-green-600">95%</span>
              </div>
              <div className="bg-slate-200 rounded-full h-1.5">
                <div className="w-[95%] h-full rounded-full bg-gradient-to-r from-indigo-500 to-indigo-400" />
              </div>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-500">Risk Level</span>
              <span className="bg-green-100 text-green-800 rounded-full px-3 py-0.5 font-bold text-[11px]">Low</span>
            </div>
          </SectionHeader>
        </>
      )}

      {activeInsightTab === "Actions" && (
        <>
          <SectionHeader title="Next Best Action">
            <div className="text-[11px] text-slate-400 mb-2">Next best actions for you</div>
            {["Assign Job", "Preview Job Proposal", "Follow-Up Call"].map(action => (
              <button
                key={action}
                className="w-full flex justify-between items-center px-3.5 py-2.5 mb-1.5 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer font-semibold text-sm text-slate-800 hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
              >
                {action} <Icons.ArrowRight size={16} />
              </button>
            ))}
          </SectionHeader>
          <SectionHeader title="Add Notes">
            <div className="text-[11px] text-slate-400 mb-2">Add notes for your reference</div>
            <button className="flex items-center gap-2 px-3.5 py-2.5 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer w-full text-sm font-semibold text-slate-500">
              <Icons.Plus size={16} /> New Note
            </button>
          </SectionHeader>
        </>
      )}
    </div>
  </div>
);

// ─── Channel config ───────────────────────────────────────────────────────────
interface ChannelConfig { label: string; count: number; icon: ReactNode; }

const channelConfigs: ChannelConfig[] = [
  { label: "All Channels", count: 12, icon: <span className="text-xs">⊞</span> },
  { label: "WhatsApp", count: 2, icon: <Icons.Whatsapp /> },
  { label: "SMS", count: 2, icon: <Icons.SMS size={14} /> },
  { label: "Email", count: 2, icon: <Icons.Email size={14} /> },
  { label: "RCS", count: 2, icon: <Icons.RCS size={14} /> },
  { label: "LinkedIn", count: 2, icon: <Icons.Linkedin /> },
  { label: "Voice", count: 2, icon: <Icons.Voice size={14} /> },
  { label: "Agents", count: 2, icon: <Icons.Agents size={14} /> },
];

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function ConversationDashboard(): JSX.Element {
  const [selectedConv, setSelectedConv] = useState<Conversation>(conversations[0]);
  const [activeChannel, setActiveChannel] = useState<number>(1);
  const [activeListTab, setActiveListTab] = useState<number>(0);
  const [activeInsightTab, setActiveInsightTab] = useState<InsightTab>("Details");
  const [showInsights, setShowInsights] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileView, setMobileView] = useState<MobileView>("list");

  useEffect(() => {
    const check = (): void => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleSelectConv = (conv: Conversation): void => {
    setSelectedConv(conv);
    if (isMobile) setMobileView("chat");
  };

  return (
    <>
      <div className="h-screen flex flex-col bg-[#FFFFFF] rounded-xl shadow overflow-hidden">

        {/* Top Bar */}
        <div className="bg-white border-b border-[#e8ecf4]  px-5 py-2.5 flex items-center justify-between">
          <h1 className="font-extrabold text-base text-slate-800 m-0">All Conversations</h1>
          <button className="flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-100 rounded-lg border-none cursor-pointer font-bold text-sm text-slate-600">
            <Avatar src="https://i.pravatar.cc/30?img=1" initials="A" color="#6366f1" size={26} />
            Admin <Icons.ChevronDown size={14} />
          </button>
        </div>

        {/* Channel Tabs */}
        <div className="bg-[#EAEDFB] border-b border-[#e8ecf4] px-4 py-2 overflow-x-auto flex gap-1.5">
          {channelConfigs.map((ch, i) => (
            <ChannelTab key={i} label={ch.label} count={ch.count} icon={ch.icon} active={activeChannel === i} onClick={() => setActiveChannel(i)} />
          ))}
        </div>

        {/* Main Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Mobile back button */}
          {isMobile && mobileView === "chat" && (
            <div className="fixed top-[130px] left-2 z-[100]">
              <button
                onClick={() => setMobileView("list")}
                className="bg-indigo-500 text-white border-none rounded-lg px-3 py-1.5 text-xs font-bold cursor-pointer"
              >
                ← Back
              </button>
            </div>
          )}

          {(!isMobile || mobileView === "list") && (
            <Sidebar selected={selectedConv} onSelect={handleSelectConv} activeTab={activeListTab} onTabChange={setActiveListTab} />
          )}

          {(!isMobile || mobileView === "chat") && selectedConv && (
            <ChatArea conv={selectedConv} onOpenInsights={() => setShowInsights(true)} isMobile={isMobile} />
          )}

          {!isMobile && <InsightsPanel activeInsightTab={activeInsightTab} onTabChange={setActiveInsightTab} />}

          {isMobile && showInsights && (
            <div
              className="fixed inset-0 bg-black/35 z-200 flex justify-end"
              onClick={() => setShowInsights(false)}
            >
              <div
                className="w-[85%] max-w-[320px] bg-white h-full overflow-y-auto"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <div className="px-4 py-3 border-b border-slate-100 flex justify-between">
                  <span className="font-extrabold">ConvoAI Insights</span>
                  <button onClick={() => setShowInsights(false)} className="border-none bg-transparent cursor-pointer text-lg">×</button>
                </div>
                <InsightsPanel activeInsightTab={activeInsightTab} onTabChange={setActiveInsightTab} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}